import React from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { answerSegments, choiceGroups, optionRows } from "../../utils/analyticsChoices";
import Legend from "../charts/Legend";
import RankedBars from "../charts/RankedBars";
import ResponsiveChart from "../charts/ResponsiveChart";
import StackedBar, { STACKED_BAR_HEIGHT } from "../charts/StackedBar";
import { formatCount, formatPercent, formatShare } from "../charts/format";
import Section from "./Section";
import StepFunnel from "./StepFunnel";

const secondary = { color: "var(--color-text-secondary)" };

const answeredRate = ({ conversations }, language) =>
  conversations.reached === 0 ? null : formatPercent((100 * conversations.answered) / conversations.reached, language);

// As conversas que chegaram a cada pergunta de um grupo, e em cada passo a parte das que responderam a
// pergunta anterior. O funil so existe com mais de uma pergunta: com uma nao ha passagem.
const GroupFunnel = ({ group }) => {
  const { translate, language } = useLanguage();
  const steps = group.questions.map((question, index) => ({
    key: question.set_id,
    label: question.name,
    value: question.conversations.reached,
    rate: index === 0 ? undefined : answeredRate(group.questions[index - 1], language) || undefined,
    rateLabel: index === 0 ? undefined : translate("analyticsChoicesAnsweredLabel", { name: group.questions[index - 1].name }),
  }));
  const title = group.scope === null ? translate("analyticsChoicesFlowNoScope") : translate("analyticsChoicesFlow", { scope: group.scope });
  return <StepFunnel title={title} steps={steps} rateColumn={translate("analyticsChoicesAnsweredColumn")} />;
};

// Uma pergunta: as vezes que se fez, a barra do que se fez com ela (os quatro segmentos com o valor e a
// parte sobre as vezes que se fez) e as opcoes tocadas. Os nomes da pergunta e das opcoes desenham-se
// como vem da config, sem `translate`.
const QuestionCard = ({ question }) => {
  const { translate, language } = useLanguage();
  const segments = answerSegments(question, translate);
  const shown = segments && segments.filter((segment) => segment.value > 0);
  const { reached, answered } = question.conversations;
  const rate = answeredRate(question, language);
  const reachedLine = `${translate(reached === 1 ? "analyticsChoicesReachedOne" : "analyticsChoicesReached", { count: formatCount(reached, language) })}; ${translate(
    answered === 1 ? "analyticsChoicesAnsweredOne" : "analyticsChoicesAnswered",
    { count: formatCount(answered, language) }
  )}${rate ? ` (${rate})` : ""}`;
  const share = (value) => formatShare(value, question.asked, language);
  return (
    <div className="rounded-xl border p-4 space-y-3" style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-surface)" }}>
      <div>
        <h3 className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
          {question.name}
          {!question.configured && <span className="font-normal" style={secondary}> ({translate("analyticsCardNoLongerConfigured")})</span>}
        </h3>
        <p className="text-xs mt-1" style={secondary}>
          {segments ? translate(question.asked === 1 ? "analyticsChoicesAskedOne" : "analyticsChoicesAsked", { count: formatCount(question.asked, language) }) : translate("analyticsChoicesNotAsked")}
        </p>
        {question.asked > 0 && (
          <p className="text-xs mt-1" style={secondary}>
            {reachedLine}
          </p>
        )}
      </div>
      {segments && (
        <>
          <Legend items={shown.map((segment) => ({ key: segment.key, color: segment.color, label: `${segment.label}: ${share(segment.value)}` }))} />
          <ResponsiveChart height={STACKED_BAR_HEIGHT}>
            {(width) => <StackedBar segments={shown} width={width} label={question.name} formatValue={share} />}
          </ResponsiveChart>
          <RankedBars
            title={translate("analyticsChoicesOptions", { name: question.name })}
            breakdown={{ total: question.tapped }}
            rows={optionRows(question, translate)}
            downloadName={question.set_id}
          />
        </>
      )}
    </div>
  );
};

// As escolhas guiadas (D8 do plano do item 14): por grupo de ambito com mais de uma pergunta, o funil, e por
// pergunta o seu cartao. A capacidade esta na propria resposta (`choices`), sem esqueleto.
const ChoicesSection = ({ choices }) => {
  const { translate } = useLanguage();
  return (
    <Section title={translate("analyticsChoices")} info={translate("analyticsChoicesNote")} className="space-y-4">
      {choices.questions.length === 0 && (
        <p className="text-sm" style={secondary}>
          {translate("analyticsChoicesNoQuestions")}
        </p>
      )}
      {choiceGroups(choices.questions).map((group) => (
        <div key={group.questions[0].set_id} className="space-y-4">
          {group.questions.length > 1 && <GroupFunnel group={group} />}
          <div className="grid gap-4 lg:grid-cols-2 items-start">
            {group.questions.map((question) => (
              <QuestionCard key={question.set_id} question={question} />
            ))}
          </div>
        </div>
      ))}
    </Section>
  );
};

export default ChoicesSection;
